import { Client } from "@/Common/Api/Client";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { PasswordCriteriaHelper } from "@/Common/Helpers/PasswordCriteriaHelper";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { ResourceProvider } from "@/Common/Resources/StaticResources";

interface IResetPasswordProps {
    onClosePopupCallBack: () => void;

    token: string;
    popupTitle: string;
    stringProvider?: ResourceProvider;
}

class ResetPasswordControlState {
    public newPwd: string;
    public confirmedNewPwd: string;
    public inprogress: boolean;
    public serverError: string;
    public tokenIsValid: boolean;
    public isAdmin: boolean;
    public passwordValidationRegex: RegExp;
    public passwordChangedSuccessfully: boolean;
    public hasPwdComplexityError: boolean;
    public passwordInstruction: string;
}

class ResetPasswordControl extends BaseReactComponent<IResetPasswordProps, ResetPasswordControlState> {
    private newPwdId = "__newpwdinputbox";

    constructor(props: IResetPasswordProps) {
        super(props);

        const initialState = new ResetPasswordControlState();
        initialState.tokenIsValid = true;
        initialState.passwordChangedSuccessfully = false;
        initialState.isAdmin = false;
        initialState.passwordValidationRegex = /^(?=.*[\d])(?=.*[A-Z])(?=.*[a-z])[\w\d!@#$%_]{8,40}$/;
        this.state = initialState;
    }

    public render(): JSX.Element {
        const hasPwdMismatchWarning =
            !(IsNullOrWhiteSpace(this.state.newPwd) || IsNullOrWhiteSpace(this.state.confirmedNewPwd))
            && this.state.newPwd !== this.state.confirmedNewPwd;

        const disableChangeButton =
            (this.state.newPwd != this.state.confirmedNewPwd)
            || IsNullOrWhiteSpace(this.state.newPwd)
            || this.state.hasPwdComplexityError
            || this.state.inprogress;

        const width = 550;
        return (
            <div className="popup" style={{ left: "50%", width: width + "px", marginLeft: -width / 2, top: "20%" }}>
                <div onKeyDown={e => this.onKeyDown(e, disableChangeButton)}>

                    <div className="p-header">
                        <h4>
                            {this.getString(this.props.popupTitle)}
                        </h4>
                    </div>

                    {this.state.tokenIsValid && !this.state.passwordChangedSuccessfully && (
                        <div className="p-body">
                            <div className="password-requirements">
                                <label>{this.state.passwordInstruction}</label>
                            </div>

                            <div className={this.buildErrorCssClass("form-group", this.state.hasPwdComplexityError)}>
                                <label className="password-labels">{this.getString("PasswordLabel")}</label>
                                <input
                                    className="form-control"
                                    type="password"
                                    value={this.state.newPwd}
                                    id={this.newPwdId}
                                    onChange={(e: any) => this.handlePasswordChanged(e.target.value)}
                                />
                            </div>

                            <div className={this.buildErrorCssClass("form-group", hasPwdMismatchWarning)}>
                                <label className="password-labels" style={{ marginTop: "10px" }}>{this.getString("ConfirmPasswordLabel")}</label>
                                <input
                                    className="form-control"
                                    type="password"
                                    value={this.state.confirmedNewPwd}
                                    onChange={(e: any) => this.handleConfirmPasswordChanged(e.target.value)}
                                />

                            </div>

                            {hasPwdMismatchWarning && (
                                <div className="error-message">
                                    <label className="label-error">{this.getString("DifferentPasswordMessage")}</label>
                                </div>
                            )}
                            {this.state.hasPwdComplexityError && (
                                <div className="error-message">
                                    <label className="label-error">{this.getString(this.state.isAdmin ? "PasswordSecurityAdmin" : "PasswordSecurity")}</label>
                                </div>
                            )}
                            {!IsNullOrWhiteSpace(this.state.serverError) && (
                                <div className="error-message">
                                    <label className="label-error">{this.getString(this.state.serverError)}</label>
                                </div>
                            )}
                        </div>
                    )}
                    {!this.state.tokenIsValid && (
                        <div className="p-body">
                            <div className="error-message">
                                <label className="label-error"> {this.getString(this.state.serverError)}</label>
                            </div>
                        </div>
                    )}
                    {this.state.passwordChangedSuccessfully && (
                        <div className="p-body">
                            <div>
                                <label className="label-success">{this.getString("PasswordChangedSuccesfullyLabel")}</label>
                            </div>
                        </div>
                    )}
                    <div className="p-footer">
                        {this.state.tokenIsValid && !this.state.passwordChangedSuccessfully && (
                            <div className="text-right">
                                <button onClick={() => this.props.onClosePopupCallBack()} className="btn btn-default push-right-5">{this.getString("CancelLabel")}</button>
                                <button className="btn btn-primary" disabled={disableChangeButton} onClick={() => this.onChangeClick()}>{this.getString("ChangeLabel")}</button>
                            </div>
                        )}

                        {(!this.state.tokenIsValid || this.state.passwordChangedSuccessfully) && (
                            <div className="text-right">
                                <button onClick={() => this.props.onClosePopupCallBack()} className="btn btn-primary">{this.getString("FinishLabel")}</button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    public componentDidMount(): void {
        this.validateTokenAction();

        window.setTimeout(() => $("#" + this.newPwdId).focus(), 200);
    }

    private handlePasswordChanged(text: string): void {
        this.mutateState(s => {
            s.newPwd = text;
            s.hasPwdComplexityError = !IsNullOrWhiteSpace(s.newPwd) && !s.passwordValidationRegex.test(s.newPwd);
            s.serverError = null;
        });
    }

    private handleConfirmPasswordChanged(text: string): void {
        this.mutateState(s => {
            s.confirmedNewPwd = text;
            s.serverError = null;
        });
    }

    private validateTokenAction(): void {
        Client.Post(
            ServerOperations.Server.Myle_PwdRecovery_Validate_Token,
            {
                Token: this.props.token
            },
            response => {
                this.mutateState(s => {
                    s.tokenIsValid = !response.Errors?.length;
                    s.serverError = response.Errors?.[0]?.Code ?? "";
                    s.isAdmin = response.IsAdmin;
                    s.passwordValidationRegex = new RegExp(response.PasswordValidationRegex);
                    s.passwordInstruction = PasswordCriteriaHelper.GetPasswordInstruction(
                        {
                            customPasswordRuleMessage: response.CustomPasswordRuleMessage,
                            customAdminPasswordRuleMessage: response.CustomAdminPasswordRuleMessage,
                            customPasswordValidationRegex: response.PasswordValidationRegex
                        },
                        response.isAdmin,
                        false,
                        this.props.stringProvider
                    );
                });
            },
            () => {
                this.mutateState(s => {
                    s.tokenIsValid = false;
                    s.serverError = "InternalServerError";
                });
            }
        );
    }

    protected getString(label: string): string {
        return this.props.stringProvider
            ? this.props.stringProvider.GetString(label)
            : cacheStore.GetString(label);
    }

    private buildErrorCssClass(baseClass: string, isError: boolean): string {
        return `${baseClass} ${isError ? "has-error" : ""}`.trim();
    }

    private onKeyDown(e: React.KeyboardEvent, disableButton: boolean): void {
        if (e.keyCode === 13 && !disableButton)
            this.onChangeClick();
        else if (e.keyCode === 27)
            this.props.onClosePopupCallBack();
    }

    private onChangeClick(): void {
        if (this.state.inprogress)
            return;

        this.mutateState(s => s.inprogress = true);

        this.changePasswordAction();
    }

    private changePasswordAction(): void {
        Client.Post(
            ServerOperations.Server.Myle_PwdRecovery_Password_Reset,
            {
                Token: this.props.token,
                Password: this.state.newPwd
            },
            response => {
                if (!response.Errors?.length)
                    this.mutateStateAfterServerResponse(s => s.passwordChangedSuccessfully = true);
                else
                    this.mutateStateAfterServerResponse(s => s.serverError = response.Errors[0]?.Code ?? "");
            },
            () => this.mutateStateAfterServerResponse(s => s.serverError = "InternalServerError")
        );
    }

    private mutateStateAfterServerResponse(mutation?: (state: ResetPasswordControlState) => void): void {
        this.mutateState(s => {
            s.inprogress = false;
            s.serverError = null;
            mutation?.(s);
        });
    }
}

export { ResetPasswordControl };
